import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { SaveViewDialog } from '@/features/views/SaveViewDialog'
import { deleteView, fetchViews, type SavedView, type ViewLayout } from '@/features/views/api'
import type { ApiError } from '@/lib/api/client'

/**
 * The saved views for a project, as a row of chips above the work.
 *
 * ADR-0003 makes the view the central abstraction — list, board and timeline
 * are three renderings of one filter — and until now there was no way to save
 * one, so the abstraction existed only in the API. A chip here is a filter plus
 * a layout; picking it does both.
 *
 * `All items` is not a stored view. It is the absence of one, kept on the left
 * so there is always a way back to everything.
 */
export function ViewBar({
  projectKey,
  projectId,
  layout,
  activeViewId,
  onPick,
}: {
  projectKey: string
  projectId: string
  layout: ViewLayout
  activeViewId: string | null
  onPick: (view: SavedView | null) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState<SavedView | null>(null)

  const views = useQuery<SavedView[], ApiError>({
    queryKey: ['views', projectKey],
    queryFn: () => fetchViews({ project: projectKey }),
    retry: false,
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteView(id),
    onSuccess: () => {
      onPick(null)
      void queryClient.invalidateQueries({ queryKey: ['views', projectKey] })
    },
  })

  const rows = views.data ?? []

  return (
    <div className="flex flex-wrap items-center gap-1.5 py-2.5">
      <button
        type="button"
        onClick={() => onPick(null)}
        aria-pressed={activeViewId === null}
        className={`rounded px-2.5 py-1 text-[12px] ${
          activeViewId === null
            ? 'bg-surface-2 text-ink-900 font-medium'
            : 'text-ink-500 hover:text-ink-900'
        }`}
      >
        {t('views.all')}
      </button>

      {rows.map((view) => (
        <span key={view.id} className="group flex items-center">
          <button
            type="button"
            onClick={() => onPick(view)}
            aria-pressed={activeViewId === view.id}
            className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-[12px] ${
              activeViewId === view.id
                ? 'bg-surface-2 text-ink-900 font-medium'
                : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {view.isShared ? <Icon name="people" className="size-3" /> : null}
            {view.name}
          </button>

          {view.canEdit ? (
            <span className="flex items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              <button
                type="button"
                onClick={() => setEditing(view)}
                title={t('views.rename')}
                aria-label={t('views.rename')}
                className="text-ink-500 hover:text-ink-900 rounded p-1"
              >
                <Icon name="pencil" className="size-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('views.deleteConfirm'))) remove.mutate(view.id)
                }}
                title={t('views.delete')}
                aria-label={t('views.delete')}
                className="text-ink-500 rounded p-1 hover:text-[color:var(--color-state-blocked)]"
              >
                <Icon name="trash" className="size-3" />
              </button>
            </span>
          ) : null}
        </span>
      ))}

      <button
        type="button"
        onClick={() => setSaving(true)}
        className="text-brand-600 ms-auto flex items-center gap-1 text-[12px]"
      >
        <Icon name="plus" className="size-3" />
        {t('views.save')}
      </button>

      {saving ? (
        <SaveViewDialog
          projectKey={projectKey}
          projectId={projectId}
          layout={layout}
          onClose={() => setSaving(false)}
          onSaved={(view) => onPick(view)}
        />
      ) : null}

      {editing === null ? null : (
        <SaveViewDialog
          projectKey={projectKey}
          projectId={projectId}
          layout={layout}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={(view) => onPick(view)}
        />
      )}
    </div>
  )
}
